import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, StopCircle, MessageSquare, ArrowLeft, Mic, Volume2, Type, Phone, Loader2, User2, Target } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import RealtimeCall from "../components/RealtimeCall";
import { useVoiceRecording } from "../hooks/useVoiceRecording";
import { IncomingCallOverlay, OutgoingCallOverlay } from "../components/chat/CallOverlays";
import VoiceControls from "../components/chat/VoiceControls";
import EndingOverlay from "../components/chat/EndingOverlay";

const ChatSession = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [ending, setEnding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("text");
  const [callState, setCallState] = useState("idle");
  const [lastVoiceStats, setLastVoiceStats] = useState(null);
  const [realtimeAvailable, setRealtimeAvailable] = useState(false);
  const [realtimeError] = useState(false);
  const scrollRef = useRef(null);

  // Voice recording lifecycle (VAD + MediaRecorder + SSE streaming upload)
  const voice = useVoiceRecording({
    sessionId,
    onUserMessage: (msg) => setMessages((prev) => [...prev, msg]),
    onAssistantUpdate: ({ tmpId, initial, delta }) => {
      setMessages((prev) => {
        if (initial) return [...prev, { id: tmpId, role: "assistant", content: delta, modality: "voice" }];
        return prev.map((m) => (m.id === tmpId ? { ...m, content: (m.content || "") + delta } : m));
      });
    },
    onAssistantFinal: ({ tmpId, message }) => {
      setMessages((prev) => {
        if (!message) return prev.filter((m) => m.id !== tmpId);
        return prev.map((m) => (m.id === tmpId ? message : m));
      });
    },
    onVoiceStats: (stats) => setLastVoiceStats(stats),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/realtime/status");
        setRealtimeAvailable(!!data?.available);
      } catch (e) { setRealtimeAvailable(false); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/training/sessions/${sessionId}`);
        setSession(data.session);
        setMessages(data.messages || []);
        if (data.session?.scenario_id) {
          const { data: sc } = await api.get(`/scenarios/${data.session.scenario_id}`);
          setScenario(sc);
        }
        if (data.feedback) {
          navigate(`/training/session/${sessionId}/feedback`);
          return;
        }
        const sessMode = data.session?.mode || "text";
        if (sessMode === "active_call") { setMode("voice"); setCallState("ringing"); }
        else if (sessMode === "passive_call") { setMode("voice"); setCallState("dialing"); }
        else { setCallState("idle"); }
      } catch (e) { toast.error("Session nicht gefunden"); navigate("/training"); }
      finally { setLoading(false); }
    })();
  }, [sessionId, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Spacebar shortcut to toggle voice recording.
  useEffect(() => {
    const onKey = (e) => {
      if (mode !== "voice" || voice.processing || callState === "ringing") return;
      if (e.code !== "Space" || e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT") return;
      e.preventDefault();
      if (voice.recording) voice.stop(); else voice.start();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, voice.recording, voice.processing, callState]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { id: "tmp-" + Date.now(), role: "user", content: text }]);
    try {
      const { data } = await api.post(`/training/sessions/${sessionId}/message`, { text });
      setMessages((prev) => {
        const cleaned = prev.filter((m) => !m.id?.startsWith("tmp-"));
        return [...cleaned, data.user_message, data.assistant_message];
      });
    } catch (e) { toast.error("Nachricht konnte nicht gesendet werden"); }
    finally { setSending(false); }
  };

  const endTraining = async () => {
    if (messages.filter((m) => m.role === "user").length < 2) {
      const ok = window.confirm("Du hast erst wenige Nachrichten gesendet. Trotzdem auswerten lassen?");
      if (!ok) return;
    }
    setEnding(true);
    voice.stopAiAudio();
    try {
      await api.post(`/training/sessions/${sessionId}/end`);
      try { window.dispatchEvent(new Event("wallet-refresh")); } catch (e) { /* noop */ }
      navigate(`/training/session/${sessionId}/feedback`);
    } catch (e) { toast.error("Auswertung fehlgeschlagen"); setEnding(false); }
  };

  const acceptCall = async () => {
    setCallState("active");
    if (realtimeAvailable && !realtimeError && (session?.mode === "active_call" || session?.mode === "passive_call")) {
      return;
    }
    // Legacy TTS opener for non-realtime fallback
    const aiOpener = messages.find((m) => m.role === "assistant");
    if (aiOpener) {
      try {
        const { data } = await api.post("/tts", { text: aiOpener.content });
        if (data.audio_base64) voice.playAudio(data.audio_base64);
      } catch (e) { console.warn("Opener TTS failed", e); }
    }
  };

  if (loading) return <div className="p-12 text-center text-novatus-text-3"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;

  const isCallMode = session?.mode === "active_call" || session?.mode === "passive_call";
  const useRealtime = realtimeAvailable && !realtimeError && isCallMode && callState === "active";

  // --- Live Realtime call (low-latency, WebRTC)
  if (useRealtime) {
    return (
      <>
        <EndingOverlay ending={ending} />
        <div className="max-w-3xl mx-auto px-3 sm:px-6 lg:px-8 pt-2 pb-0 sm:py-8">
          <RealtimeCall
            sessionId={sessionId}
            sessionMode={session.mode}
            scenarioTitle={scenario?.title || "Live-Anruf"}
            onEnd={endTraining}
            onTranscriptUpdate={(msg) => setMessages((prev) => [...prev, msg])}
          />
        </div>
      </>
    );
  }

  // --- Pre-call overlays (active_call → incoming, passive_call → outgoing)
  if (callState === "dialing" && session?.mode === "passive_call") {
    return (
      <OutgoingCallOverlay
        scenario={scenario}
        realtimeAvailable={realtimeAvailable}
        onStart={acceptCall}
        onCancel={() => navigate("/training")}
      />
    );
  }
  if (callState === "ringing" && session?.mode === "active_call") {
    return (
      <IncomingCallOverlay
        scenario={scenario}
        realtimeAvailable={realtimeAvailable}
        onAccept={acceptCall}
        onReject={() => navigate("/training")}
      />
    );
  }

  const headerSubtitle = (() => {
    if (session?.mode === "active_call") return "Du spielst Kunde · Anruf läuft";
    if (session?.mode === "passive_call") return `Anruf läuft · ${scenario?.customer_type}`;
    return `Typ: ${scenario?.customer_type}`;
  })();

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-2 pb-0 sm:py-8">
      <EndingOverlay ending={ending} />
      <button onClick={() => navigate("/training")} className="hidden sm:inline-flex text-sm text-novatus-text-3 hover:text-novatus-green items-center gap-1 mb-4" data-testid="back-to-training-btn">
        <ArrowLeft className="w-4 h-4" /> Zurück zu Trainings
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className={`order-2 lg:order-1 lg:col-span-4 xl:col-span-3 ${mode === "voice" ? "hidden lg:block" : ""}`}>
          <div className="nv-card p-6 lg:sticky lg:top-24" data-testid="scenario-info-panel">
            <span className="label-eyebrow">Aktives Szenario</span>
            <h2 className="font-display font-bold text-xl text-white mt-2 leading-tight">{scenario?.title}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full diff-${scenario?.difficulty}`}>{scenario?.difficulty}</span>
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2 flex items-center gap-1"><User2 className="w-3 h-3" /> {scenario?.customer_type}</span>
            </div>
            <p className="text-sm text-novatus-text-2 mt-4 leading-relaxed">{scenario?.description}</p>
            <div className="mt-5 pt-4 border-t border-novatus">
              <p className="label-eyebrow flex items-center gap-1.5"><Target className="w-3 h-3 text-novatus-green" /> Lernziel</p>
              <p className="text-sm text-white mt-1.5 leading-snug">{scenario?.learning_goal}</p>
            </div>
            {scenario?.typical_objections?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-novatus">
                <p className="label-eyebrow">Typische Einwände</p>
                <ul className="text-xs text-novatus-text-2 mt-2 space-y-1">
                  {scenario.typical_objections.slice(0, 4).map((o, i) => (<li key={`obj-${i}-${o.slice(0, 30)}`}>• {o}</li>))}
                </ul>
              </div>
            )}
          </div>
        </aside>

        <section className="order-1 lg:order-2 lg:col-span-8 xl:col-span-9">
          <div className="nv-card flex flex-col overflow-hidden h-[calc(100dvh-84px)] sm:h-[calc(100vh-180px)]">
            {/* Header */}
            <div className="border-b border-novatus px-3 sm:px-5 py-2 sm:py-3 flex items-center justify-between flex-wrap gap-2 sm:gap-3 shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${session?.mode === "active_call" ? "bg-novatus-green/20 border border-novatus-green/50" : "bg-novatus-green/15 border border-novatus-green/40"}`}>
                  {session?.mode === "active_call" ? <Phone className="w-4 h-4 text-novatus-green" /> : <MessageSquare className="w-4 h-4 text-novatus-green" />}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-white truncate">
                    {session?.mode === "active_call" ? "KI-Verkäufer (Demo)" : "KI-Kunde"}
                  </p>
                  <p className="text-xs text-novatus-text-3 truncate">{headerSubtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setMode("text")} className={`pill-tab ${mode === "text" ? "active" : ""}`} data-testid="mode-text-btn">
                    <Type className="w-3.5 h-3.5" /> Text
                  </button>
                  <button onClick={() => setMode("voice")} className={`pill-tab ${mode === "voice" ? "active" : ""}`} data-testid="mode-voice-btn">
                    <Mic className="w-3.5 h-3.5" /> Voice
                  </button>
                </div>
                <button
                  onClick={endTraining}
                  disabled={ending}
                  className="h-9 px-3 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-400 border border-red-500/40 font-semibold flex items-center gap-1.5 text-xs sm:text-sm transition-colors shrink-0"
                  data-testid="end-training-header-btn"
                  title="Training beenden und Feedback erhalten"
                >
                  {ending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StopCircle className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline">{ending ? "Bewertet..." : "Training beenden"}</span>
                  <span className="sm:hidden">{ending ? "..." : "Beenden"}</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={m.id || i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[82%] flex items-end gap-2">
                    <div className={`group px-4 py-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bubble-user rounded-br-sm" : "bubble-ai rounded-bl-sm"}`} data-testid={`message-${m.role}-${i}`}>
                      {m.content}
                      {m.role === "assistant" && (
                        <button
                          onClick={() => voice.playMessageAudio(m.content)}
                          className="ml-2 inline-flex items-center text-novatus-text-3 hover:text-novatus-green align-middle"
                          title="Vorlesen"
                          data-testid={`tts-btn-${i}`}
                        ><Volume2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {(sending || voice.processing) && (
                <div className="flex justify-start">
                  <div className="bubble-ai px-4 py-3 rounded-2xl rounded-bl-sm text-sm flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-novatus-green animate-pulse" />
                    <span className="w-2 h-2 rounded-full bg-novatus-green animate-pulse" style={{ animationDelay: ".2s" }} />
                    <span className="w-2 h-2 rounded-full bg-novatus-green animate-pulse" style={{ animationDelay: ".4s" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            {mode === "text" ? (
              <form onSubmit={send} className="border-t border-novatus p-4 flex items-end gap-3 shrink-0">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Deine Antwort an den Kunden..."
                  rows={2}
                  className="nv-input flex-1 resize-none"
                  data-testid="chat-input"
                  disabled={sending || ending}
                />
                <button
                  type="submit"
                  disabled={sending || !input.trim() || ending}
                  className="btn-green h-12 w-12 rounded-xl flex items-center justify-center disabled:opacity-50"
                  data-testid="chat-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            ) : (
              <VoiceControls voice={voice} ending={ending} lastVoiceStats={lastVoiceStats} />
            )}
          </div>
          {mode === "text" && (
            <p className="text-xs text-novatus-text-3 mt-3 text-center">
              <kbd className="px-1.5 py-0.5 bg-novatus-card-2 rounded border border-novatus">Enter</kbd> senden, <kbd className="px-1.5 py-0.5 bg-novatus-card-2 rounded border border-novatus">Shift+Enter</kbd> Zeilenumbruch
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default ChatSession;
